import cx from "classnames";
import { ContentState, EditorState, convertToRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';

import { ToolbarNameButton } from './components/toolbar-name-button';
import { ToolbarLinkButton } from './components/toolbar-link-button';

import { ToolbarCustomButtonType } from "./text-editor.types";

import './text-editor.styles.scss';

export const TextEditor = (props) => {
  const { onChange, defaultValue, value, onBlur, classes, className, toolbarCustomButtons } = props;
  const [editorState, setEditorState] = React.useState();

  React.useEffect(() => {
    if (defaultValue) {
      const contentBlock = htmlToDraft(defaultValue);
  
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        const state = EditorState.createWithContent(contentState);
  
        setEditorState(state);
      }
    }
  }, []);

  React.useEffect(() => {
    if (defaultValue) {
      return
    }
    
    const contentBlock = htmlToDraft(value ?? "");
    const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);

    let state;

    if (!editorState) {
      state = EditorState.createWithContent(contentState);
    } else if (handleDraftToHtml(editorState) !== value) {
      state = EditorState.push(editorState, contentState);
    }
    
    if (state) {
      setEditorState(state);
    }

  }, [value]);

  const handleDraftToHtml = React.useCallback((state) => {
    return draftToHtml(convertToRaw(state.getCurrentContent())); 
  }, []);

  const handleChange = React.useCallback((state) => {
    setEditorState(state);

    if (onChange) {
      onChange(handleDraftToHtml(state));
    }
  }, [onChange, handleDraftToHtml]);

  const handleBlur = React.useCallback((data) => {
    if (onBlur) {
      onBlur(handleDraftToHtml(editorState));
    }
  }, [onBlur, editorState, handleDraftToHtml]);

  const predefinedButtons = React.useMemo(() => {
    return {
      [ToolbarCustomButtonType.name]: <ToolbarNameButton className="TextEditor__toolbar__button" />,
      [ToolbarCustomButtonType.link]: <ToolbarLinkButton className="TextEditor__toolbar__button" />,
    }
  }, []);

  const defaultButtons = React.useMemo(() => {
    return [
      predefinedButtons[ToolbarCustomButtonType.name],
      predefinedButtons[ToolbarCustomButtonType.link],
    ]
  }, [predefinedButtons]);

  const processedToolbarCustomButtons = React.useMemo(() => {
    return toolbarCustomButtons?.map((x) => {
      if (predefinedButtons[x.type]) {
        return predefinedButtons[x.type];
      }
      return x.component;
    })
  }, [predefinedButtons, toolbarCustomButtons]);

  if (!editorState) {
    return null;
  }

  return (
    <Editor
      onEditorStateChange={handleChange}
      editorState={editorState}
      wrapperClassName={cx("TextEditor", className)}
      toolbarClassName="TextEditor__toolbar"
      editorClassName={cx("TextEditor__work-area", classes?.workArea)}
      toolbarCustomButtons={processedToolbarCustomButtons || defaultButtons}
      onBlur={handleBlur}
      toolbar={{
        inline: {
          bold: { className: "TextEditor__toolbar__button" },
          italic: { className: "TextEditor__toolbar__button" },
          underline: { className: "TextEditor__toolbar__button" },
          strikethrough: { className: "TextEditor__toolbar__button" },
          monospace: { className: "TextEditor__toolbar__button" },
          superscript: { className: "TextEditor__toolbar__button" },
          subscript: { className: "TextEditor__toolbar__button" },
        },
        list: {
          unordered: { className: "TextEditor__toolbar__button" },
          ordered: { className: "TextEditor__toolbar__button" },
          indent: { className: "TextEditor__toolbar__button" },
          outdent: { className: "TextEditor__toolbar__button" },
        },
        textAlign: {
          left: { className: "TextEditor__toolbar__button" },
          center: { className: "TextEditor__toolbar__button" },
          right: { className: "TextEditor__toolbar__button" },
          justify: { className: "TextEditor__toolbar__button" },
        },
        colorPicker: { className: "TextEditor__toolbar__button" },
        link: {
          link: { className: "TextEditor__toolbar__button" },
          unlink: { className: "TextEditor__toolbar__button" },
        },
        embedded: { className: "TextEditor__toolbar__button" },
        emoji: { className: "TextEditor__toolbar__button" },
        image: { className: "TextEditor__toolbar__button" },
        remove: { className: "TextEditor__toolbar__button" },
        history: {
          undo: { className: "TextEditor__toolbar__button" },
          redo: { className: "TextEditor__toolbar__button" },
        },
      }}
    />
  );
};
